/**
 * @fileoverview Deadline Calendar Animation
 *
 * Purpose: A stylised, looping recreation of Morgan removing missed-deadline
 * risk for the homepage feature card ("Guided workflows, aligned team, zero
 * missed deadlines").
 *
 * It tells a three-beat story:
 *
 *  1. Starting   - the practitioner starts a new job: a stylised "New Job"
 *                  intake card fills in (company, ACN, appointment type,
 *                  appointment date) and a cursor clicks "Create job".
 *  2. Calendar   - the job's month view auto-populates with the statutory key
 *                  dates derived from that appointment. Colour-coded event
 *                  badges drop into the correct day cells of a full-size month
 *                  grid, exactly as the real calendar would generate them.
 *  3. Tasks      - the view widens to the workspace: a list of upcoming tasks
 *                  across every job, each tagged with its matter and a
 *                  due-date pill, sorted soonest-first.
 *
 * Accuracy: the calendar beat uses the real derived key dates for a Creditors'
 * Voluntary Liquidation appointed Tue 1 Sep 2026 (see
 * convex/keyDates/derivations.ts):
 *   - Appointment date                          → day 0      (appointment)
 *   - Notice of appointment (Form 505)          → +1 BD      (lodgement)
 *   - ATO + authority notification              → +1 BD      (lodgement)
 *   - ROCAP receipt due (CVL 5 BD)              → +5 BD      (lodgement)
 *   - Initial notification to creditors (10 BD) → +10 BD     (lodgement)
 *   - DIRRI (Form 531) lodgement                → +10 BD     (lodgement)
 *   - ROCAP lodgement (Form 507 Part A)         → receipt+10 (lodgement)
 * Titles mirror the system's `LODGEMENT_RULES` titles verbatim. The grid is
 * Sunday-start to match the app's month view (WEEK_DAYS in
 * calendar-month-view.tsx).
 *
 * This is a marketing stylisation — no live data — sized to the feature-card
 * container. It reuses Morgan's calendar colour language (blue appointment
 * anchors, orange reporting & lodgements, purple meetings, red action
 * deadlines — see src/calendar/categoryMeta.ts).
 *
 * Respects prefers-reduced-motion by rendering the final "tasks" view.
 *
 * Used by: FeatureCardsSection (-sections.feature-cards.tsx)
 */

import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import {
  AlertTriangle,
  CalendarDays,
  CalendarPlus,
  FileText,
  Flag,
  Layers,
  ListChecks,
  MousePointer2,
  Sparkles,
  Users,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/utils/misc";

// ---------------------------------------------------------------------------
// Category styling (mirrors src/calendar/categoryMeta.ts)
// ---------------------------------------------------------------------------

type Category = "appointment" | "lodgement" | "meeting" | "action";

interface CategoryStyle {
  icon: LucideIcon;
  /** Soft pill background + foreground for badges and leading icons. */
  pill: string;
  /** Solid dot colour. */
  dot: string;
}

const CATEGORY_STYLES: Record<Category, CategoryStyle> = {
  appointment: {
    icon: Flag,
    pill: "bg-blue-100 text-blue-700",
    dot: "bg-blue-500",
  },
  lodgement: {
    icon: FileText,
    pill: "bg-orange-100 text-orange-700",
    dot: "bg-orange-500",
  },
  meeting: {
    icon: Users,
    pill: "bg-purple-100 text-purple-700",
    dot: "bg-purple-500",
  },
  action: {
    icon: AlertTriangle,
    pill: "bg-red-100 text-red-700",
    dot: "bg-red-500",
  },
};

// ---------------------------------------------------------------------------
// Content (stylised — no live data)
// ---------------------------------------------------------------------------

/** The matter the practitioner starts in beat 1. */
const NEW_JOB = {
  name: "Brightwater Logistics Pty Ltd",
  acn: "123 456 789",
  appointmentType: "Creditors' Voluntary Liquidation",
  appointmentDate: "1 Sep 2026",
} as const;

/** Intake fields that fill in, in order, during beat 1. */
const INTAKE_FIELDS = [
  { label: "Company name", value: NEW_JOB.name },
  { label: "ACN", value: NEW_JOB.acn, mono: true },
  { label: "Appointment type", value: NEW_JOB.appointmentType },
  { label: "Appointment date", value: NEW_JOB.appointmentDate, mono: true },
] as const;

interface DeadlineEvent {
  id: string;
  /** Day of September the deadline falls on (drives placement in the grid). */
  day: number;
  /** Full title — mirrors the system's derived title verbatim. */
  title: string;
  /** Short label shown in the (truncated) month-cell badge. */
  short: string;
  category: Category;
}

/**
 * The real derived key dates for a CVL appointed Tue 1 Sep 2026, in reveal
 * order (chronological). See file header for the statutory offsets.
 */
const DEADLINES: DeadlineEvent[] = [
  {
    id: "appointment",
    day: 1,
    title: "Appointment date",
    short: "Appointment",
    category: "appointment",
  },
  {
    id: "form505",
    day: 2,
    title: "Notice of appointment (Form 505)",
    short: "Form 505",
    category: "lodgement",
  },
  {
    id: "ato",
    day: 2,
    title: "ATO + authority notification",
    short: "ATO notice",
    category: "lodgement",
  },
  {
    id: "rocap_receipt",
    day: 8,
    title: "ROCAP receipt due",
    short: "ROCAP receipt",
    category: "lodgement",
  },
  {
    id: "creditor_notice",
    day: 15,
    title: "Initial notification to creditors",
    short: "Creditor notice",
    category: "lodgement",
  },
  {
    id: "dirri",
    day: 15,
    title: "DIRRI (Form 531) lodgement",
    short: "DIRRI",
    category: "lodgement",
  },
  {
    id: "rocap_lodge",
    day: 22,
    title: "ROCAP lodgement (Form 507 Part A)",
    short: "ROCAP lodge",
    category: "lodgement",
  },
] as const;

interface UpcomingTask {
  id: string;
  title: string;
  job: string;
  due: string;
  category: Category;
  /** Urgency tone for the due pill. */
  tone: "now" | "soon" | "later";
}

/**
 * Upcoming tasks across every job (beat 3), soonest-first. Titles mirror the
 * system's derived titles (LODGEMENT_RULES) and category meta.
 */
const TASKS: UpcomingTask[] = [
  {
    id: "t1",
    title: "First creditors meeting (s436E)",
    job: "Apex Manufacturing Pty Ltd",
    due: "Tomorrow",
    category: "meeting",
    tone: "now",
  },
  {
    id: "t2",
    title: "Notice of appointment (Form 505)",
    job: "Brightwater Logistics Pty Ltd",
    due: "In 2 days",
    category: "lodgement",
    tone: "soon",
  },
  {
    id: "t3",
    title: "s588FF — last date to commence",
    job: "Coastline Retail Group",
    due: "In 3 days",
    category: "action",
    tone: "soon",
  },
  {
    id: "t4",
    title: "DIRRI (Form 531) lodgement",
    job: "Vertex Property Holdings",
    due: "In 5 days",
    category: "lodgement",
    tone: "later",
  },
  {
    id: "t5",
    title: "Statutory Report by Liquidator (Form 5601)",
    job: "Northwind Traders",
    due: "In 9 days",
    category: "lodgement",
    tone: "later",
  },
] as const;

const TONE_STYLES: Record<UpcomingTask["tone"], string> = {
  now: "bg-red-100 text-red-700",
  soon: "bg-amber-100 text-amber-700",
  later: "bg-primary/10 text-primary/70",
};

// --- Timing (module scope so the progress line stays in sync with the run) ---
/** Stagger between intake fields auto-filling. */
const FIELD_STAGGER_MS = 360;
const FILL_END_MS = INTAKE_FIELDS.length * FIELD_STAGGER_MS;
/** Cursor travels to and clicks "Create job". */
const CREATE_CLICK_MS = FILL_END_MS + 750;
const CREATING_END_MS = CREATE_CLICK_MS + 600;

/** Stagger between each deadline dropping into the calendar. */
const DEADLINE_STAGGER_MS = 300;
const CALENDAR_POPULATE_END_MS =
  CREATING_END_MS + 450 + DEADLINES.length * DEADLINE_STAGGER_MS;
/** How long the filled calendar holds before widening to the workspace. */
const CALENDAR_HOLD_MS = 2600;
const TASKS_START_MS = CALENDAR_POPULATE_END_MS + CALENDAR_HOLD_MS;

/** Stagger between each upcoming task dropping into the list. */
const TASK_STAGGER_MS = 280;
const TASKS_POPULATE_END_MS =
  TASKS_START_MS + 400 + TASKS.length * TASK_STAGGER_MS;
/** How long the finished task list is held before the cycle repeats. */
const TASKS_HOLD_MS = 3400;

/** Total length of one play-through — drives the progress line + orchestrator. */
export const DEADLINE_CALENDAR_ANIMATION_CYCLE_MS =
  TASKS_POPULATE_END_MS + TASKS_HOLD_MS;

type Phase = "creating" | "calendar" | "tasks";

interface DeadlineCalendarAnimationProps {
  /**
   * When false, the animation rests on the finished "tasks" view (no playback,
   * no progress line). The parent orchestrator toggles this so only one card
   * animates at a time.
   */
  isActive?: boolean;
}

export function DeadlineCalendarAnimation({
  isActive = true,
}: DeadlineCalendarAnimationProps) {
  const reducedMotion = useReducedMotion() ?? false;

  const [phase, setPhase] = useState<Phase>("creating");
  const [filledFields, setFilledFields] = useState(0);
  const [visibleDeadlines, setVisibleDeadlines] = useState(0);
  const [visibleTasks, setVisibleTasks] = useState(0);
  // Cursor position (x %, y px within the body) for the "Create job" click.
  const [cursor, setCursor] = useState({ x: 60, y: 60, visible: false });
  // True while the cursor is pressing the Create button (drives the click dip).
  const [pressed, setPressed] = useState(false);
  // Bumped at the start of every play-through so the progress line restarts.
  const [cycle, setCycle] = useState(0);

  const timers = useRef<Array<ReturnType<typeof setTimeout>>>([]);
  // Refs used to land the cursor precisely on the Create button.
  const bodyRef = useRef<HTMLDivElement>(null);
  const createBtnRef = useRef<HTMLSpanElement>(null);

  /**
   * Measure the Create button's centre relative to the animation body so the
   * cursor's tip sits on top of it regardless of card size. Returns null while
   * the button isn't mounted (falls back to the previous position).
   */
  const pointAtCreateButton = () => {
    const body = bodyRef.current;
    const btn = createBtnRef.current;
    if (!body || !btn) return null;
    const b = body.getBoundingClientRect();
    const r = btn.getBoundingClientRect();
    return {
      x: ((r.left - b.left + r.width * 0.5) / b.width) * 100,
      y: r.top - b.top + r.height * 0.5,
      visible: true,
    };
  };

  useEffect(() => {
    // Resting / static state: the finished upcoming-tasks view.
    if (reducedMotion || !isActive) {
      timers.current.forEach(clearTimeout);
      timers.current = [];
      setPhase("tasks");
      setFilledFields(INTAKE_FIELDS.length);
      setVisibleDeadlines(DEADLINES.length);
      setVisibleTasks(TASKS.length);
      setCursor((c) => ({ ...c, visible: false }));
      setPressed(false);
      return;
    }

    const clearAll = () => {
      timers.current.forEach(clearTimeout);
      timers.current = [];
    };

    const after = (ms: number, fn: () => void) => {
      timers.current.push(setTimeout(fn, ms));
    };

    const runCycle = () => {
      // Reset
      setPhase("creating");
      setCycle((c) => c + 1);
      setFilledFields(0);
      setVisibleDeadlines(0);
      setVisibleTasks(0);
      setCursor({ x: 60, y: 60, visible: false });
      setPressed(false);

      // Beat 1: the new-job form fills in field by field.
      INTAKE_FIELDS.forEach((_, i) => {
        after(i * FIELD_STAGGER_MS, () => setFilledFields(i + 1));
      });
      // Cursor appears mid-card, travels onto the Create button, then clicks.
      after(FILL_END_MS + 150, () =>
        setCursor({ x: 55, y: 150, visible: true }),
      );
      after(FILL_END_MS + 400, () =>
        setCursor((c) => pointAtCreateButton() ?? { ...c, visible: true }),
      );
      // Press once the cursor has arrived (travel transition is 0.55s).
      after(FILL_END_MS + 1000, () => setPressed(true));
      after(FILL_END_MS + 1200, () => setPressed(false));

      // Beat 2: calendar auto-populates with the derived statutory deadlines.
      after(CREATING_END_MS, () => {
        setPhase("calendar");
        setCursor((c) => ({ ...c, visible: false }));
      });
      DEADLINES.forEach((_, i) => {
        after(CREATING_END_MS + 450 + i * DEADLINE_STAGGER_MS, () =>
          setVisibleDeadlines(i + 1),
        );
      });

      // Beat 3: widen to the workspace — upcoming tasks across all jobs.
      after(TASKS_START_MS, () => setPhase("tasks"));
      TASKS.forEach((_, i) => {
        after(TASKS_START_MS + 400 + i * TASK_STAGGER_MS, () =>
          setVisibleTasks(i + 1),
        );
      });

      // Loop.
      after(DEADLINE_CALENDAR_ANIMATION_CYCLE_MS, runCycle);
    };

    runCycle();
    return clearAll;
  }, [reducedMotion, isActive]);

  const playing = isActive && !reducedMotion;
  const creating = phase === "creating";
  const calendar = phase === "calendar";
  const tasks = phase === "tasks";

  return (
    <div className="absolute inset-0 overflow-hidden rounded-lg bg-white dark:bg-card">
      {/* Progress line — fills along the bottom while this animation plays */}
      {playing && (
        <motion.div
          key={cycle}
          className="absolute inset-x-0 bottom-0 z-50 h-[3px] bg-primary"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{
            duration: DEADLINE_CALENDAR_ANIMATION_CYCLE_MS / 1000,
            ease: "linear",
          }}
          style={{ originX: 0 }}
        />
      )}

      <div className="flex h-full w-full flex-col">
        {/* Title bar */}
        <div className="flex items-center gap-2 border-b border-primary/10 bg-primary/[0.03] px-3 py-2">
          <div className="flex gap-1.5">
            <span className="h-2 w-2 rounded-full bg-red-400/70" />
            <span className="h-2 w-2 rounded-full bg-amber-400/70" />
            <span className="h-2 w-2 rounded-full bg-emerald-400/70" />
          </div>
          <span className="ml-1 text-[11px] font-semibold text-primary">
            {creating ? "New Job" : calendar ? "Calendar" : "Upcoming"}
          </span>
          {calendar && (
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="ml-auto flex items-center gap-1 rounded-md bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700"
            >
              <Sparkles className="h-2.5 w-2.5" />
              Auto-populated
            </motion.span>
          )}
          {tasks && (
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="ml-auto flex items-center gap-1 rounded-md bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-primary"
            >
              <Layers className="h-2.5 w-2.5" />
              All jobs
            </motion.span>
          )}
        </div>

        {/* Body */}
        <div
          ref={bodyRef}
          className="relative flex min-h-0 flex-1 flex-col overflow-hidden p-3"
        >
          {/* Beat 1: new-job intake form */}
          {creating && (
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="flex min-h-0 flex-1 flex-col"
            >
              <div className="mb-2 flex items-center gap-1.5 px-0.5">
                <CalendarPlus className="h-3.5 w-3.5 shrink-0 text-primary" />
                <span className="text-[10.5px] font-semibold text-primary">
                  Start a new job
                </span>
                <span className="ml-auto text-[8.5px] font-medium text-primary/40">
                  Step 1 of 1
                </span>
              </div>

              <div className="flex flex-1 flex-col justify-center gap-1.5">
                {INTAKE_FIELDS.map((field, i) => {
                  const isFilled = i < filledFields;
                  return (
                    <div
                      key={field.label}
                      className="rounded-md border border-primary/10 bg-primary/[0.02] px-2.5 py-1.5"
                    >
                      <div className="text-[8px] font-semibold uppercase tracking-wider text-primary/40">
                        {field.label}
                      </div>
                      <div className="mt-0.5 flex h-3.5 items-center">
                        {isFilled ? (
                          <motion.span
                            initial={{
                              opacity: 0,
                              backgroundColor: "hsl(var(--primary) / 0.16)",
                            }}
                            animate={{
                              opacity: 1,
                              backgroundColor: "hsl(var(--primary) / 0)",
                            }}
                            transition={{ duration: 0.6 }}
                            className={cn(
                              "truncate rounded-sm text-[10px] font-semibold text-foreground",
                              "mono" in field && field.mono && "font-mono",
                            )}
                          >
                            {field.value}
                          </motion.span>
                        ) : (
                          <span className="block h-1.5 w-1/3 rounded-full bg-primary/10" />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Create job button */}
              <div className="mt-2 flex justify-end">
                <motion.span
                  ref={createBtnRef}
                  animate={pressed ? { scale: 0.92 } : { scale: 1 }}
                  transition={{ duration: 0.18 }}
                  className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-[10px] font-semibold text-primary-foreground"
                >
                  <CalendarPlus className="h-3 w-3" />
                  Create job
                </motion.span>
              </div>
            </motion.div>
          )}

          {/* Beat 2: calendar pre-populated with deadlines (full month grid) */}
          {calendar && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
              className="flex min-h-0 flex-1 flex-col"
            >
              <div className="mb-2 flex items-center gap-1.5 px-0.5">
                <CalendarDays className="h-3.5 w-3.5 shrink-0 text-primary" />
                <span className="text-[10.5px] font-semibold text-primary">
                  September 2026
                </span>
                <span className="ml-auto truncate text-[9.5px] font-medium text-foreground/55">
                  {NEW_JOB.name}
                </span>
              </div>

              <MonthGrid visibleDeadlines={visibleDeadlines} />

              {/* Legend — ties the badge colours to their categories */}
              <div className="mt-1.5 flex items-center gap-3 px-0.5">
                <LegendDot category="appointment" label="Appointment" />
                <LegendDot
                  category="lodgement"
                  label="Reporting & lodgements"
                />
              </div>
            </motion.div>
          )}

          {/* Beat 3: upcoming tasks across all jobs */}
          {tasks && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
              className="flex min-h-0 flex-1 flex-col"
            >
              <div className="mb-2 flex items-center gap-1.5 px-0.5">
                <ListChecks className="h-3.5 w-3.5 shrink-0 text-primary" />
                <span className="text-[10.5px] font-semibold text-primary">
                  Upcoming deadlines
                </span>
                <span className="ml-auto rounded bg-primary/[0.06] px-1.5 py-0.5 text-[8.5px] font-semibold text-primary/55">
                  {TASKS.length} across {TASKS.length} jobs
                </span>
              </div>

              <div className="flex min-h-0 flex-1 flex-col justify-center gap-1.5">
                {TASKS.map((task, i) => {
                  const isVisible = i < visibleTasks;
                  const style = CATEGORY_STYLES[task.category];
                  const Icon = style.icon;
                  return (
                    <motion.div
                      key={task.id}
                      initial={false}
                      animate={
                        isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 8 }
                      }
                      transition={{ duration: 0.3, ease: "easeOut" }}
                      className="flex items-center gap-2 rounded-md border border-primary/10 bg-white px-2 py-1.5 dark:bg-card"
                    >
                      <span
                        className={cn(
                          "flex h-6 w-6 shrink-0 items-center justify-center rounded-md",
                          style.pill,
                        )}
                      >
                        <Icon className="h-3 w-3" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[9.5px] font-semibold leading-tight text-foreground">
                          {task.title}
                        </p>
                        <p className="truncate text-[8.5px] leading-tight text-primary/45">
                          {task.job}
                        </p>
                      </div>
                      <span
                        className={cn(
                          "shrink-0 rounded px-1.5 py-0.5 text-[8.5px] font-semibold tabular-nums",
                          TONE_STYLES[task.tone],
                        )}
                      >
                        {task.due}
                      </span>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* Animated cursor for the "Create job" gesture */}
          {cursor.visible && (
            <motion.div
              className="pointer-events-none absolute z-40 drop-shadow"
              initial={false}
              animate={{ left: `${cursor.x}%`, top: `${cursor.y}px` }}
              transition={{ duration: 0.55, ease: "easeInOut" }}
            >
              <MousePointer2 className="h-4 w-4 fill-white text-foreground" />
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}

const WEEK_DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/**
 * MonthGrid — a full-size, Sunday-start September 2026 month view (matching the
 * app's `CalendarMonthView`). Deadline badges drop into the correct day cells
 * as the calendar populates, truncating in-cell just like the real month view.
 */
function MonthGrid({ visibleDeadlines }: { visibleDeadlines: number }) {
  // September 2026: 1 Sep is a Tuesday → 2 leading blanks (Sun, Mon) on a
  // Sunday-start grid. 30 days, padded to complete the final week.
  const LEADING_BLANKS = 2;
  const DAYS_IN_MONTH = 30;
  const cells: Array<number | null> = [
    ...Array.from({ length: LEADING_BLANKS }, () => null),
    ...Array.from({ length: DAYS_IN_MONTH }, (_, i) => i + 1),
  ];
  while (cells.length % 7 !== 0) cells.push(null);

  // Group the (revealed) deadlines by day, preserving their reveal order.
  const eventsByDay = new Map<number, DeadlineEvent[]>();
  DEADLINES.forEach((deadline, i) => {
    if (i >= visibleDeadlines) return;
    const list = eventsByDay.get(deadline.day) ?? [];
    list.push(deadline);
    eventsByDay.set(deadline.day, list);
  });

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-md border border-primary/10">
      {/* Weekday header */}
      <div className="grid grid-cols-7 border-b border-primary/10 bg-primary/[0.03]">
        {WEEK_DAYS.map((d, i) => (
          <span
            key={i}
            className="py-1 text-center text-[7px] font-semibold uppercase tracking-wider text-primary/40"
          >
            {d}
          </span>
        ))}
      </div>

      {/* Day cells */}
      <div
        className="grid min-h-0 flex-1 grid-cols-7"
        style={{
          gridTemplateRows: `repeat(${cells.length / 7}, minmax(0, 1fr))`,
        }}
      >
        {cells.map((day, i) => {
          const dayEvents = day != null ? (eventsByDay.get(day) ?? []) : [];
          return (
            <div
              key={day != null ? `d${day}` : `b${i}`}
              className={cn(
                "min-w-0 border-b border-r border-primary/[0.07] p-0.5",
                i % 7 === 6 && "border-r-0",
                day == null && "bg-primary/[0.015]",
              )}
            >
              {day != null && (
                <>
                  <span className="block px-0.5 text-[8px] font-semibold leading-none text-foreground/55">
                    {day}
                  </span>
                  <div className="mt-0.5 space-y-0.5">
                    {dayEvents.map((event) => {
                      const style = CATEGORY_STYLES[event.category];
                      return (
                        <motion.span
                          key={event.id}
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{
                            type: "spring",
                            stiffness: 460,
                            damping: 24,
                          }}
                          title={event.title}
                          className={cn(
                            "block truncate rounded-[2px] px-1 text-[6.5px] font-semibold leading-[1.45]",
                            style.pill,
                          )}
                        >
                          {event.short}
                        </motion.span>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** A small colour swatch + label used in the calendar legend. */
function LegendDot({ category, label }: { category: Category; label: string }) {
  return (
    <span className="flex items-center gap-1 text-[7.5px] font-medium text-primary/45">
      <span
        className={cn(
          "h-1.5 w-1.5 rounded-full",
          CATEGORY_STYLES[category].dot,
        )}
      />
      {label}
    </span>
  );
}
